import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";

import { AuthScreen } from "@/components/auth/auth-screen";
import { useAdminPassword } from "@/hooks/use-admin-password";
import { Button, Input } from "@/ui";

const schema = z.object({
  email: z.string().email({ message: "Enter a valid email address." }),
});

type ForgotPasswordFormData = z.infer<typeof schema>;

const ForgotPasswordPage = () => {
  const { isPending, isSuccess, mutate } = useAdminPassword().sendPasswordResetLink();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = (data) => {
    mutate(data);
  };

  return (
    <AuthScreen title="Forgot your password?">
      {isSuccess ? (
        <div className="flex flex-col items-start gap-4">
          <p className="text-sm text-content-muted">
            If an account with that email exists, we&apos;ve sent password reset instructions. The
            link expires in 60 minutes.
          </p>

          <Link to="/login">
            <Button size="md" variant="primary">
              Back to login
            </Button>
          </Link>
        </div>
      ) : (
        <form
          className="max-w-form flex flex-col items-start justify-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <p className="text-sm text-content-muted">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>

          <Controller
            control={control}
            name="email"
            render={({ field }) => {
              return (
                <Input
                  containerClassName="w-full"
                  error={errors.email?.message}
                  id="email"
                  label="Email"

                  placeholder="Enter your email address"
                  {...field}
                />
              );
            }}
          />

          <Button
            className="mt-2 w-full"
            isLoading={isPending}
            size="md"
            type="submit"
            variant="primary"
          >
            Send reset link
          </Button>

          <Link
            className="rounded-sm text-sm text-accent-text underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none"
            to="/login"
          >
            Back to login
          </Link>
        </form>
      )}
    </AuthScreen>
  );
};

export const Route = createFileRoute("/_guest/forgot-password/")({
  component: ForgotPasswordPage,
});
